// The three books. Each one turns { name, child, family, message } into a list of pages.
// A page is { kind, bg, art, words } and, when it shows one person, who: kind "pic" is a picture above a band of words,
// "text" is big words on a bold colour, "cover" is the front of the book.
// Pages are made in pairs so the book opens as spreads: a question on the left, the answer on the right.

window.KBBooks = (() => {
  const { C, INK, BRIGHTS, esc, animal, person, partyHat, place, thing, word, star, page } = window.KBArt;
  const BOLD = [C.pink, C.blue, C.orange, C.green, C.purple, C.teal, C.red];
  const TINTS = ['#ffe9a8', '#cfe3fb', '#ffd6de', '#d3efd9', '#e6dafa', '#ffdcc2'];
  const NIGHT = '#2a2466';
  const PAPER = '#fff8ec';

  const pic = (bg, art, words) => ({ kind: 'pic', bg, art: page(art), words });
  const text = (bg, art, words, kind = 'text') => ({ kind, bg, art: page(art), words });
  const big = (line) => `<p class="kb-big${line.length > 14 ? ' kb-big--long' : ''}">${line}</p>`;
  const line = (words) => `<p class="kb-line${words.length > 26 ? ' kb-line--long' : ''}">${words}</p>`;
  const small = (words) => `<p class="kb-small">${words}</p>`;
  const verse = (words) => `<p class="kb-rhyme">${words}</p>`;
  const list = (names) => (names.length < 2 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`);
  const stars = (points, colour) => points.map(([x, y, s]) => `<g transform="translate(${x} ${y}) scale(${s})" filter="url(#kb-paint)">${star(colour)}</g>`).join('');
  const confetti = (seed = 0) => Array.from({ length: 16 }, (_, i) => `<circle cx="${(i * 97 + seed * 31) % 380 + 10}" cy="${(i * 53 + seed * 17) % 250 + 12}" r="${5 + (i % 3) * 2}" fill="${BRIGHTS[(i + seed) % 8]}"/>`).join('');
  const bunting = (y = 34) =>
    `<path d="M -10 ${y - 14} Q 200 ${y + 26} 410 ${y - 14}" stroke="${INK}" stroke-width="2.5" fill="none" opacity="0.5"/><g filter="url(#kb-paint)">${Array.from({ length: 9 }, (_, i) => {
      const x = 10 + i * 45;
      const top = y - 14 + Math.sin(((x + 14) / 400) * Math.PI) * 20;
      return `<path d="M ${x} ${top} l 30 0 l -15 34 Z" fill="${BRIGHTS[i % 8]}"/>`;
    }).join('')}</g>`;

  const child = (x, hat) => {
    const figure = person(x.child, x.childPhoto);
    return hat ? { ...figure, front: (figure.front || '') + partyHat(hat, 112) } : figure;
  };
  const cover = (bg, kicker, title, art, light) => ({ kind: light ? 'cover kb-page--light' : 'cover', bg, art: page(art), words: `<p class="kb-kicker">${kicker}</p><h3 class="kb-title${title.length > 20 ? ' kb-title--long' : ''}">${title}</h3>` });
  const dedication = (x) => {
    const from = x.family.length ? small(`With love from ${esc(list(x.family.map((member) => member.call)))}`) : '';
    return { who: 'child', ...pic(TINTS[2], stars([[70, 70, 0.5], [330, 60, 0.7], [56, 190, 0.35], [344, 200, 0.4]], '#fff') + place(child(x), 200, 310, 0.62), `<p class="kb-line">${esc(x.message || `This book was made for ${x.name}.`)}</p>${from}`) };
  };

  // ---------- 1. The Big Name Parade ----------
  // Each letter has one or two animals: [drawing, name, sound, line]. A repeated letter brings the next one.

  const ZOO = {
    A: [['ant', 'Ant', 'Tip, tip, tip!', 'Ant is tiny. The A is not!'], ['antelope', 'Antelope', 'Prance, prance!', 'Antelope leaps over with an A.']],
    B: [['bear', 'Bear', 'Shuffle, shuffle!', 'Bear hugs the B so it cannot get lost.']],
    C: [['cow', 'Cow', 'Moo, moo, moo!', 'Cow carried the C all the way from the farm.']],
    D: [['deer', 'Deer', 'Skip, skip, skip!', 'Deer leaps in with a D.'], ['dog', 'Dog', 'Woof! Woof!', 'Dog wags in with a D.']],
    E: [['elephant', 'Elephant', 'Thump! Thump!', 'Elephant lifts the E up high.'], ['eagle', 'Eagle', 'Swoop!', 'Eagle soars in with an E.']],
    F: [['frog', 'Frog', 'Boing! Boing!', 'Frog hops in with an F.']],
    G: [['goat', 'Goat', 'Trit-trot, trit-trot!', 'Goat wants to nibble the G. No, Goat!']],
    H: [['hen', 'Hen', 'Cluck, cluck, cluck!', 'Hen kept the H warm all night.'], ['hippo', 'Hippo', 'Splosh! Splosh!', 'Hippo wades in with an H.']],
    I: [['ibis', 'Ibis', 'Flap, flap, flap!', 'Ibis flew the I across the river.']],
    J: [['jackal', 'Jackal', 'Pitter, patter!', 'Jackal jumps for joy with a J.']],
    K: [['kingfisher', 'Kingfisher', 'Zip! Zoom!', 'Kingfisher dives in with a K.'], ['kitten', 'Kitten', 'Mew, mew!', 'Kitten pounces on the K.']],
    L: [['lion', 'Lion', 'ROAR!', 'Lion roars, "Here is the L!"'], ['leopard', 'Leopard', 'Prowl, prowl!', 'Leopard creeps in with an L.']],
    M: [['monkey', 'Monkey', 'Ooh-ooh! Aah-aah!', 'Monkey swings by with an M.'], ['mouse', 'Mouse', 'Squeak, squeak!', 'Mouse scampers in with an M.']],
    N: [['nilgai', 'Nilgai', 'Clip, clop!', 'Nilgai trots in with an N.'], ['nightingale', 'Nightingale', 'Tweet, tweet!', 'Nightingale sings in with an N.']],
    O: [['owl', 'Owl', 'Hoo! Hoo!', 'Owl stayed up late to bring the O.'], ['otter', 'Otter', 'Splash, splash!', 'Otter floats in with an O.']],
    P: [['peacock', 'Peacock', 'Swish, swish!', 'Peacock fans his tail for the P.'], ['parrot', 'Parrot', 'Squawk!', 'Parrot flaps in with a P.']],
    Q: [['quail', 'Quail', 'Scurry, scurry!', 'Quail is quick with the Q.']],
    R: [['rhino', 'Rhino', 'Stomp! Stomp!', 'Rhino rumbles in with an R.'], ['rabbit', 'Rabbit', 'Hop, hop, hop!', 'Rabbit bounces in with an R.']],
    S: [['snake', 'Snake', 'Hiss, hiss, hiss!', 'Snake slides in with an S. Sssss!'], ['squirrel', 'Squirrel', 'Scamper, scamper!', 'Squirrel scurries in with an S.']],
    T: [['tiger', 'Tiger', 'Pad, pad, pad!', 'Tiger brings the T on tiptoe.']],
    U: [['unicorn', 'Unicorn', 'Clippety-clop!', 'Unicorn gallops in with a U. Yes, really!']],
    V: [['vulture', 'Vulture', 'Whoosh!', 'Vulture glides down with a V.']],
    W: [['wolf', 'Wolf', 'Awooo!', 'Wolf howls for the W.']],
    X: [['fox', 'Fox', 'Rustle, rustle!', 'Nobody starts with X. So Fox brought it. FoX!']],
    Y: [['yak', 'Yak', 'Huff, puff!', 'Yak carried the Y down the mountain.']],
    Z: [['zebra', 'Zebra', 'Clip, clop, clip!', 'Zebra zigzags in with a Z.']],
  };

  // A name written in another script is paraded as written, one letter with its marks at a time,
  // and these animals take turns to carry them.
  const TURNS = ['elephant', 'tiger', 'peacock', 'monkey', 'lion', 'parrot', 'bear', 'owl', 'rabbit', 'deer', 'frog', 'zebra'].map((kind) => Object.values(ZOO).flat().find((entry) => entry[0] === kind));
  const CARRIES = [(who, L) => `${who} brings the ${L}.`, (who, L) => `${who} holds the ${L} up high.`, (who, L) => `Here comes ${who} with the ${L}.`];
  const PLAIN = { Æ: 'AE', Ø: 'O', Œ: 'OE', Ł: 'L', Đ: 'D', Þ: 'TH' };
  const JOINS = /[्্੍્୍్್്]‍?$/; // a letter ending like this is half of the next one
  const RTL = /[֐-ࣿיִ-ﻼ]/;

  // The letters the parade spells: English letters with accents dropped (Zoë is Z, O, E),
  // or the name's own letters when it is written in another script. LOVE stands in for a name with no letters.
  function lettersOf(name) {
    const english = name.toUpperCase().replace(/[ÆØŒŁĐÞ]/g, (ch) => PLAIN[ch]).normalize('NFD').replace(/[^A-Z]/g, '');
    const own = [];
    (name.normalize('NFC').match(/\p{L}[\p{M}‌‍]*/gu) || []).forEach((unit) => {
      if (/^[a-z]/i.test(unit.normalize('NFD'))) return;
      if (own.length && JOINS.test(own.at(-1))) own[own.length - 1] += unit;
      else own.push(unit);
    });
    if (own.length > english.length) return { letters: own.slice(0, 12), kind: 'own' };
    if (english) return { letters: english.slice(0, 12).split(''), kind: 'english' };
    return { letters: ['L', 'O', 'V', 'E'], kind: 'love' };
  }
  // How much to shrink a letter that is wider than one capital.
  const fitFor = (L) => {
    const wide = [...L].filter((ch) => !/[\p{Mn}‌‍]/u.test(ch)).length;
    return wide < 2 ? 1 : wide === 2 ? 0.7 : 0.5;
  };

  function parade(x) {
    const { letters, kind: spells } = lettersOf(x.name);
    const spreads = letters.length <= 8;
    const seen = {};
    const name = esc(x.name);
    // Who carries each letter, in order.
    const steps = letters.map((L, i) => {
      seen[L] = (seen[L] || 0) + 1;
      const fit = fitFor(L);
      const colour = BOLD[i % BOLD.length];
      if (spells === 'own') {
        const [kind, who, sound] = TURNS[i % TURNS.length];
        return { L, fit, colour, kind, who, sound, told: CARRIES[i % CARRIES.length](who, L), again: seen[L] > 1 };
      }
      const [kind, who, sound, say] = ZOO[L][(seen[L] - 1) % ZOO[L].length];
      const visit = Math.ceil(seen[L] / ZOO[L].length); // how many times this animal has now come
      const hat = visit === 2 ? C.purple : visit > 2 ? C.teal : null;
      const told = visit === 1 ? say : visit === 2 ? `${who} is back, with one more ${L}!` : `${who} AGAIN? Yes! Another ${L}!`;
      return { L, fit, colour, kind, who, sound, told, hat, again: seen[L] > 1 };
    });

    const trio = steps.slice(0, 3);
    const pages = [
      cover(C.yellow, 'A name parade', `${name}${x.name.endsWith('s') ? '’' : '’s'} Big Parade`, trio.map((step, i) => place(animal(step.kind, { letter: step.L, colour: BOLD[i], fit: step.fit }), trio.length === 1 ? 200 : trio.length === 2 ? 130 + i * 140 : 82 + i * 118, 404, i === 1 || trio.length < 3 ? 0.5 : 0.42)).join('')),
      dedication(x),
      text(C.pink, confetti(5), `${big('BOOM! BOOM!')}${line('A parade is coming.')}`),
      pic(TINTS[0], bunting() + place(child(x), 200, 310, 0.66), `${line(`And it is all for ${name}!`)}${small('Who is coming first?')}`),
    ];

    steps.forEach(({ L, fit, colour, kind, who, sound, told, hat, again }, i) => {
      const art = place(animal(kind, { letter: L, colour, hat, fit }), 200, 284, 0.66);
      const tint = TINTS[(i + 1) % TINTS.length];
      if (spreads) {
        pages.push(text(colour, `<text x="200" y="${200 + 172 * fit}" text-anchor="middle" font-size="${420 * fit}" font-family="'Baloo 2', sans-serif" font-weight="800" fill="#fff" opacity="0.16">${L}</text>`, `${big(esc(sound))}${line(again ? `Who has ANOTHER ${L}?` : `Who has the ${L}?`)}`));
        pages.push(pic(tint, art, `${line(`It’s ${who}!`)}${small(esc(told))}`));
      } else {
        pages.push(pic(tint, art, `${line(`${esc(sound)} It’s ${who}!`)}${small(esc(told))}`));
      }
    });

    const perRow = Math.min(letters.length, 6);
    const rows = Math.ceil(letters.length / perRow);
    const size = Math.min(104, 376 / perRow);
    const flags = letters
      .map((L, i) => {
        const inRow = Math.min(perRow, letters.length - Math.floor(i / perRow) * perRow);
        const across = (i % perRow) - (inRow - 1) / 2;
        const fx = 200 + (RTL.test(L) ? -across : across) * size;
        const fy = (rows === 1 ? 130 : 82) + Math.floor(i / perRow) * size * 1.3 + Math.sin(i * 1.7) * 6;
        const half = size * 0.44;
        return `<g filter="url(#kb-paint)"><path d="M ${fx - half} ${fy - half} h ${half * 2} v ${half * 1.9} l ${-half} ${-half * 0.5} l ${-half} ${half * 0.5} Z" fill="${BOLD[i % BOLD.length]}"/></g><text x="${fx}" y="${fy + half * 0.72}" text-anchor="middle" font-size="${size * 0.72 * fitFor(L)}" font-family="'Baloo 2', sans-serif" font-weight="800" fill="#fff">${L}</text>`;
      })
      .join('');
    const spelled = letters.join(' · ');
    if (!spreads && letters.length % 2) pages.push(text(C.blue, confetti(3), `${big('What a parade!')}${line('Look at all those letters.')}`));
    pages.push(pic(PAPER, `<path d="M -10 ${rows === 1 ? 70 : 28} Q 200 ${rows === 1 ? 96 : 54} 410 ${rows === 1 ? 70 : 28}" stroke="${INK}" stroke-width="2.5" fill="none" opacity="0.4"/>` + flags, `${line(spelled)}${small(spells === 'love' ? `That spells LOVE, and it is all for ${name}.` : `That spells ${name}!`)}`));
    pages.push(pic(TINTS[0], confetti() + place(child(x, C.pink), 200, 310, 0.66), `${line(`Hooray for ${name}!`)}${small('The best name in the whole parade. The end.')}`));
    return pages;
  }

  // ---------- 2. Who Loves Me? ----------

  const drops = [[120, 190], [170, 240], [220, 190], [270, 240], [320, 190], [80, 240]].map(([x, y], i) => thing('drop', x, y, 0.42, i)).join('');
  const LOVES = [
    ['as big as the sky', 'Sky-big, and bigger still!', '#9ad4f5', () => thing('sun', 300, 96, 1.5) + thing('cloud', 112, 120, 1.5) + thing('cloud', 240, 228, 1.1)],
    ['as sweet as a mango', 'Mango-sweet, and sweeter!', TINTS[3], () => thing('mango', 200, 158, 2.7)],
    ['as high as a kite can fly', 'Higher than the highest kite!', TINTS[1], () => thing('cloud', 96, 210, 1.2) + thing('kite', 226, 112, 2, 6)],
    ['as loud as a dhol', 'DHOOM! DHOOM! That loud!', TINTS[0], () => thing('dhol', 200, 160, 2.5)],
    ['as deep as the sea', 'Deeper than the fish can swim!', '#bfe6ee', () => thing('fish', 130, 110, 1.4, 0) + thing('fish', 280, 170, 1.1, 3) + thing('fish', 150, 230, 0.9, 5) + thing('wave', 100, 30, 1.2) + thing('wave', 300, 30, 1.2)],
    ['as warm as a fresh roti', 'Warm and soft and puffed right up!', TINTS[5], () => `<path d="M 160 60 q 14 -18 0 -36 M 200 54 q 14 -18 0 -36 M 240 60 q 14 -18 0 -36" stroke="#fff" stroke-width="7" stroke-linecap="round" fill="none"/>` + thing('roti', 200, 170, 2.6)],
    ['as strong as a banyan tree', 'Strong enough to hold you tight!', TINTS[0], () => thing('tree', 200, 168, 2.9)],
    ['more than all the stars', 'More than you can ever count!', NIGHT, () => stars([[80, 70, 0.7], [200, 120, 1.1], [320, 70, 0.6], [120, 210, 0.5], [290, 220, 0.8], [200, 30, 0.35], [50, 150, 0.3], [350, 150, 0.35]])],
    ['as much as the peacock loves the rain', 'Enough to dance all day!', TINTS[1], () => thing('cloud', 200, 90, 2.4) + drops],
    ['as bright as a diya', 'Bright enough to light the night!', '#3a2a78', () => `<circle cx="200" cy="130" r="120" fill="${C.yellow}" opacity="0.18"/>` + thing('diya', 200, 190, 2.5)],
  ];

  function wholoves(x) {
    const name = esc(x.name);
    const family = x.family.length ? x.family : [{ call: 'Mummy', look: window.KBArt.lookFor('Mummy', 1) }, { call: 'Papa', look: window.KBArt.lookFor('Papa', 2) }];
    const hearts = thing('heart', 70, 80, 0.5, 2) + thing('heart', 330, 70, 0.7) + thing('heart', 340, 210, 0.4, 4) + thing('heart', 56, 210, 0.35);
    const pages = [
      cover(C.pink, 'A family book', `Who Loves ${name}?`, thing('heart', 60, 230, 0.45, 7) + thing('heart', 340, 250, 0.6, 7) + thing('heart', 330, 150, 0.3, 7) + place(child(x), 200, 404, 0.6), true),
      dedication(x),
      text(C.blue, `<g opacity="0.22">${thing('heart', 200, 210, 3.6, 7)}</g>`, `${big(`Somebody loves ${name}.`)}${line('Not a little. A LOT!')}`),
      pic(TINTS[0], hearts + place(child(x), 200, 310, 0.66), `${line('But who?')}${small('Turn the page and see.')}`),
    ];
    const spreads = family.length <= 8;
    family.forEach((member, i) => {
      const [ask, reply, bg, art] = LOVES[i % LOVES.length];
      const who = esc(member.call);
      const portrait = place(person(member.look, member.photo), 200, 318, 0.72);
      // In a big family everyone gets one page; the last person gets a spread if that keeps the pages even.
      if (spreads || (i === family.length - 1 && family.length % 2)) {
        pages.push(pic(bg, art(), `${line(`Who loves ${name}`)}${small(`${ask}?`)}`));
        pages.push({ who: member, ...pic(TINTS[(i + 2) % TINTS.length], portrait, `${line(`${who} does!`)}${small(reply)}`) });
      } else {
        pages.push({ who: member, ...pic(TINTS[(i + 2) % TINTS.length], portrait, `${line(`${who} loves ${name}`)}${small(`${ask}.`)}`) });
      }
    });
    const everyone = [...family.slice(0, 7).map((member) => person(member.look, member.photo)), child(x)];
    const back = everyone.slice(0, everyone.length > 3 ? Math.floor(everyone.length / 2) : 0);
    const front = everyone.slice(back.length);
    const step = 372 / front.length;
    const scale = Math.min(0.5, step / 250);
    const row = (figures, bottom) => figures.map((figure, i) => place(figure, 200 + (i - (figures.length - 1) / 2) * step, bottom, scale)).join('');
    pages.push(pic(TINTS[1], row(back, 318 - scale * 250) + row(front, 318), `${line(`Who loves ${name}?`)}${small('Everybody does!')}`));
    pages.push(pic(TINTS[2], hearts + place(child(x), 200, 310, 0.66), `${line(`And ${name} loves them right back.`)}${small('The end.')}`));
    return pages;
  }

  // ---------- 3. Count to Ten ----------

  const NUMBERS = [
    ['one', 'sun', '#9ad4f5', (x) => ['One yellow sun comes up to play.', `Good morning, ${x.name}! It’s a brand new day.`]],
    ['two', 'parrot', TINTS[0], () => ['Two green parrots, bright and loud,', 'squawk hello to a sleepy cloud.']],
    ['three', 'kite', TINTS[1], (x) => ['Three bright kites fly way up high.', `Wave to them, ${x.name}, as they go by!`]],
    ['four', 'mango', TINTS[3], (x, m) => ['Four ripe mangoes, sweet to eat.', `${m(0)} has cut them. What a treat!`]],
    ['five', 'frog', '#bfe6ee', () => ['Five fat frogs jump in the rain.', 'Splash! Splash! Splash! And splash again.']],
    ['six', 'bangle', TINTS[2], (x, m) => ['Six round bangles go jingle-jing.', `${m(1)} claps along and starts to sing.`]],
    ['seven', 'laddoo', TINTS[4], (x) => ['Seven sweet laddoos on a plate.', `One for ${x.name}! The rest can wait.`]],
    ['eight', 'fish', '#bfe6ee', () => ['Eight little fish swim round and round.', 'They blow their bubbles without a sound.']],
    ['nine', 'diya', '#3a2a78', (x, m) => ['Nine little diyas, all in a row.', `${m(2)} lights them. Watch them glow!`]],
    ['ten', 'star', NIGHT, (x) => ['Ten tiny stars say, “Time for bed.”', `Goodnight, ${x.name}, you sleepyhead.`]],
  ];
  const ROWS = [[1], [2], [3], [2, 2], [3, 2], [3, 3], [4, 3], [4, 4], [3, 3, 3], [5, 5]];

  function count(x) {
    const name = esc(x.name);
    const calls = x.family.length ? x.family.map((member) => member.call) : ['Mummy', 'Papa', 'Dadi'];
    const m = (i) => calls[i % calls.length];
    const digits = (y, size) => [1, 2, 3].map((n, i) => word(n, 96 + i * 104, y + (i % 2) * 18, size, ['#fff', C.yellow, C.pink][i])).join('');
    const pages = [
      cover(C.blue, 'A counting book', `${name} Counts to Ten`, digits(300, 150) + stars([[60, 372, 0.4], [200, 380, 0.3], [344, 366, 0.45]]), true),
      dedication(x),
    ];
    NUMBERS.forEach(([say, kind, bg, rhyme], index) => {
      const n = index + 1;
      const rows = ROWS[index];
      const cell = Math.min(360 / Math.max(...rows), 270 / rows.length);
      const scale = Math.min(1.5, cell / 112);
      let i = 0;
      const things = rows
        .map((inRow, r) => Array.from({ length: inRow }, (_, c) => thing(kind, 200 + (c - (inRow - 1) / 2) * cell, 150 + (r - (rows.length - 1) / 2) * cell, scale, i++)).join(''))
        .join('');
      const [first, second] = rhyme(x, m);
      pages.push(text(BOLD[index % BOLD.length], word(n, 200, 258, n === 10 ? 250 : 300, '#fff'), big(say), 'number'));
      const named = [3, 5, 8].indexOf(index); // the verses for four, six and nine name someone in the family
      pages.push({ who: named >= 0 && x.family.length ? x.family[named % x.family.length] : null, ...pic(bg, things, `${verse(esc(first))}${verse(esc(second))}`) });
    });
    const chart = NUMBERS.map((_, i) => word(i + 1, 56 + (i % 5) * 72, 120 + Math.floor(i / 5) * 120, 86, BOLD[i % BOLD.length])).join('');
    pages.push(pic(PAPER, chart, `${line('One to ten!')}${small(`Can you count them all again, ${name}?`)}`));
    pages.push(pic(NIGHT, stars([[70, 60, 0.5], [330, 70, 0.7], [50, 190, 0.35], [350, 200, 0.4], [200, 34, 0.3]]) + place(child(x), 200, 310, 0.66), `${line(`Ten out of ten, ${name}!`)}${small('The end.')}`));
    return pages;
  }

  const BOOKS = {
    parade: { handle: 'the-big-name-parade', make: parade, family: false, title: (name) => `${name}${name.endsWith('s') ? '’' : '’s'} Big Parade` },
    wholoves: { handle: 'who-loves-me', make: wholoves, family: true, title: (name) => `Who Loves ${name}?` },
    count: { handle: 'count-to-ten', make: count, family: true, title: (name) => `${name} Counts to Ten` },
  };
  const keyFor = (handle) => Object.keys(BOOKS).find((key) => BOOKS[key].handle === handle) || 'parade';

  // ---------- a book for the printer ----------
  // A printed book is stapled from folded sheets, so it needs a multiple of four pages. An endpaper goes
  // inside the front cover, which keeps each question on a left page and its answer on the right.
  // Two more go before the back cover when the count needs them, and the back cover comes last.

  const BLEED = (400 * 3) / 210; // 3 mm of extra picture on every side of a 21 cm page, in picture units
  const SPECKS = Array.from({ length: 16 }, (_, i) => [62 + (i % 4) * 94 + (Math.floor(i / 4) % 2) * 28 - 14, 56 + Math.floor(i / 4) * 96, 0.3 + ((i + Math.floor(i / 4)) % 3) * 0.1]);

  function printPages(key, x) {
    const pages = BOOKS[key].make(x);
    const front = pages[0];
    const endpaper = () => ({ kind: 'text', bg: front.bg, art: page(`<g opacity="0.4">${stars(SPECKS, '#fff')}</g>`), words: '', plain: true });
    const back = { ...front, art: page(stars([[70, 330, 0.5], [200, 300, 0.8], [330, 340, 0.4]], '#fff')), words: `<p class="kb-kicker">Made with love for</p><h3 class="kb-title${x.name.length > 10 ? ' kb-title--long' : ''}">${esc(x.name)}</h3>`, plain: true };
    const more = (pages.length + 2) % 4 ? 2 : 0;
    return [front, endpaper(), ...pages.slice(1), ...Array.from({ length: more }, endpaper), back].map((p) => ({ ...p, art: p.art.replace('viewBox="0 0 400 400"', `viewBox="${-BLEED} ${-BLEED} ${400 + BLEED * 2} ${400 + BLEED * 2}"`) }));
  }

  // The HTML of one page; number is its place in the book, starting at 1.
  // Pass withArt false to leave the picture out; the studio adds it when the page comes into view.
  const pageHtml = (p, number, withArt = true) =>
    `<div class="kb-page kb-page--${p.kind}" style="--kb-bg:${p.bg}"><div class="kb-page__art">${withArt ? p.art : ''}</div><div class="kb-page__words">${p.words}</div>${number > 1 ? `<span class="kb-page__number">${number}</span>` : ''}</div>`;

  return { BOOKS, keyFor, pageHtml, printPages, pages: (key, x) => BOOKS[key].make(x), title: (key, name) => BOOKS[key].title(name) };
})();
